// Avoid TS moduleResolution issues with keycloak-js type exports in this repo setup.
// We only need a minimal surface of the Keycloak instance here.
export type KeycloakLike = {
  updateToken: (minValiditySeconds: number) => Promise<boolean>;
  login: (options?: any) => Promise<void>;
  token?: string;
  refreshToken?: string;
};

export type RefreshOutcome =
  | {ok: true; refreshed: boolean}
  | {ok: false; reason: string; error?: unknown};

type TokenPair = {token?: string; refreshToken?: string};

type RecoverOptions = {
  /**
   * Called when we believe the failure is network-related and the UI can show
   * a retry banner/spinner instead of forcing a re-login immediately.
   */
  onNetworkError?: () => void;
  /**
   * For cases where a login redirect is too aggressive, callers can decide.
   * Default: true.
   */
  loginOnFailure?: boolean;
  /**
   * When true, uses prompt=login to force UI.
   * Default: false.
   */
  forcePromptLogin?: boolean;
};

let inFlightRefresh: Promise<RefreshOutcome> | null = null;
let recoveryUntilTs = 0;

const TOKEN_STORAGE_KEY = 'hit_kc_token';
const REFRESH_TOKEN_STORAGE_KEY = 'hit_kc_refresh_token';

// Cross-tab refresh lock to avoid concurrent refresh token use.
const REFRESH_LOCK_KEY = 'hit_kc_refresh_lock_v1';
const REFRESH_LOCK_TTL_MS = 15_000;
const TOKEN_BROADCAST_CHANNEL = 'hit_kc_tokens_v1';

const registeredKeycloaks = new Set<KeycloakLike>();
let broadcastChannel: BroadcastChannel | null = null;
let broadcastInitDone = false;

function getTabId(): string {
  try {
    const k = 'hit_kc_tab_id';
    const existing = sessionStorage.getItem(k);
    if (existing) return existing;
    const id =
      (globalThis.crypto as any)?.randomUUID?.() ||
      `${Math.random().toString(16).slice(2)}-${Date.now().toString(16)}`;
    sessionStorage.setItem(k, id);
    return id;
  } catch {
    return `${Math.random().toString(16).slice(2)}-${Date.now().toString(16)}`;
  }
}

function safeJsonParse<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function nowMs(): number {
  return Date.now();
}

function initBroadcastOnce() {
  if (broadcastInitDone) return;
  broadcastInitDone = true;
  try {
    broadcastChannel = new BroadcastChannel(TOKEN_BROADCAST_CHANNEL);
    broadcastChannel.onmessage = (ev) => {
      const data = ev?.data as any;
      if (!data) return;

      if (data?.type === 'kc_tokens') {
        const token: string | undefined = data?.token;
        const refreshToken: string | undefined = data?.refreshToken;
        // Persist for reloads and update any registered in-memory Keycloak instances.
        try {
          if (token) localStorage.setItem(TOKEN_STORAGE_KEY, token);
          if (refreshToken)
            localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refreshToken);
        } catch {
          // ignore
        }
        for (const kc of registeredKeycloaks) {
          if (token) kc.token = token;
          if (refreshToken) kc.refreshToken = refreshToken;
        }
        return;
      }
    };
  } catch {
    broadcastChannel = null;
  }
}

export function registerKeycloakForTokenSync(keycloak: KeycloakLike) {
  initBroadcastOnce();
  registeredKeycloaks.add(keycloak);
}

function broadcastTokens(tokens: TokenPair) {
  if (!broadcastChannel) return;
  try {
    broadcastChannel.postMessage({
      type: 'kc_tokens',
      token: tokens.token,
      refreshToken: tokens.refreshToken,
      ts: nowMs(),
    });
  } catch {
    // ignore
  }
}

type RefreshLock = {owner: string; expiresAt: number};

function tryAcquireRefreshLock(): boolean {
  const owner = getTabId();
  const now = nowMs();
  try {
    const existing = safeJsonParse<RefreshLock>(
      localStorage.getItem(REFRESH_LOCK_KEY)
    );
    if (existing && existing.expiresAt > now && existing.owner !== owner) {
      return false;
    }
    const lock: RefreshLock = {owner, expiresAt: now + REFRESH_LOCK_TTL_MS};
    localStorage.setItem(REFRESH_LOCK_KEY, JSON.stringify(lock));
    // Verify we still own it (best-effort, avoids obvious races).
    const verify = safeJsonParse<RefreshLock>(
      localStorage.getItem(REFRESH_LOCK_KEY)
    );
    return !!verify && verify.owner === owner;
  } catch {
    // If storage is blocked, fall back to per-tab behavior.
    return true;
  }
}

function releaseRefreshLock() {
  const owner = getTabId();
  try {
    const existing = safeJsonParse<RefreshLock>(
      localStorage.getItem(REFRESH_LOCK_KEY)
    );
    if (existing?.owner === owner) localStorage.removeItem(REFRESH_LOCK_KEY);
  } catch {
    // ignore
  }
}

async function waitForOtherTabRefresh(maxWaitMs = 6_000): Promise<TokenPair> {
  const start = nowMs();
  const initial = getStoredKeycloakTokens();
  while (nowMs() - start < maxWaitMs) {
    await new Promise((r) => setTimeout(r, 150));
    const current = getStoredKeycloakTokens();
    if (
      (current.token && current.token !== initial.token) ||
      (current.refreshToken && current.refreshToken !== initial.refreshToken)
    ) {
      return current;
    }
    // Also bail out early if lock disappeared.
    const lock = safeJsonParse<RefreshLock>(
      localStorage.getItem(REFRESH_LOCK_KEY)
    );
    if (!lock || lock.expiresAt <= nowMs()) {
      return current;
    }
  }
  return getStoredKeycloakTokens();
}

function safeNow(): number {
  return Date.now();
}

export function getStoredKeycloakTokens(): {
  token?: string;
  refreshToken?: string;
} {
  try {
    // IMPORTANT: use localStorage so tokens are shared across tabs.
    // Fallback to sessionStorage for backward compatibility with already-open tabs.
    const token =
      localStorage.getItem(TOKEN_STORAGE_KEY) ||
      sessionStorage.getItem(TOKEN_STORAGE_KEY) ||
      undefined;
    const refreshToken =
      localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY) ||
      sessionStorage.getItem(REFRESH_TOKEN_STORAGE_KEY) ||
      undefined;
    return {token, refreshToken};
  } catch {
    return {};
  }
}

export function storeKeycloakTokens(keycloak: KeycloakLike) {
  try {
    if (keycloak.token) localStorage.setItem(TOKEN_STORAGE_KEY, keycloak.token);
    if (keycloak.refreshToken)
      localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, keycloak.refreshToken);
    broadcastTokens({
      token: keycloak.token,
      refreshToken: keycloak.refreshToken,
    });
  } catch {
    // ignore
  }
}

export function clearStoredKeycloakTokens() {
  try {
    // Remove both for cleanup/migration.
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
    sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    sessionStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
  } catch {
    // ignore
  }
}

function removeCookie(name: string) {
  document.cookie = `${name}=;path=/;expires=Thu, 01 Jan 1970 00:00:01 GMT;secure;samesite=strict`;
}

function clearKeycloakCookieArtifacts() {
  // Legacy cookies used by the app to persist tokens; keep clearing them so
  // users don't get stuck with stale tokens after session expiry.
  removeCookie('kc_token');
  removeCookie('kc_refreshToken');
}

function isLikelyNetworkError(error: unknown): boolean {
  if (!error) return false;
  const msg = typeof error === 'string' ? error : (error as any)?.message;
  return (
    typeof msg === 'string' &&
    (msg.includes('An unexpected HTTP error occurred') ||
      msg.includes('NetworkError') ||
      msg.includes('Failed to fetch') ||
      msg.includes('timeout'))
  );
}

function describeError(error: unknown): string {
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as any).message;
    if (typeof message === 'string') return message;
  }
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

async function doLogin(keycloak: KeycloakLike, forcePromptLogin: boolean) {
  // Clear flags used by legacy logging to avoid odd states after relogin.
  try {
    sessionStorage.removeItem('keycloak_real_login');
    sessionStorage.removeItem('has_logged_login');
  } catch {
    // ignore
  }

  if (forcePromptLogin) {
    await keycloak.login({prompt: 'login'} as any);
  } else {
    await keycloak.login();
  }
}

export async function refreshTokenOrRecover(
  keycloak: KeycloakLike,
  minValiditySeconds: number,
  opts: RecoverOptions = {}
): Promise<RefreshOutcome> {
  initBroadcastOnce();
  registeredKeycloaks.add(keycloak);

  const now = safeNow();
  if (now < recoveryUntilTs) {
    return {
      ok: false,
      reason: 'recovery_in_progress',
    };
  }

  if (inFlightRefresh) return inFlightRefresh;

  inFlightRefresh = (async () => {
    if (!tryAcquireRefreshLock()) {
      const tokens = await waitForOtherTabRefresh();
      if (tokens.token) keycloak.token = tokens.token;
      if (tokens.refreshToken) keycloak.refreshToken = tokens.refreshToken;
      // We didn't refresh ourselves, but we likely got updated tokens.
      return {ok: true, refreshed: false};
    }
    try {
      const refreshed = await keycloak.updateToken(minValiditySeconds);
      // Persist latest tokens for reloads (especially important when Keycloak is on a different domain).
      if (refreshed) storeKeycloakTokens(keycloak);
      return {ok: true, refreshed};
    } catch (error) {
      const errStr = describeError(error);

      // If it's likely network, let the UI handle retry logic.
      if (isLikelyNetworkError(error)) {
        opts.onNetworkError?.();
        return {ok: false, reason: 'network_error', error};
      }

      // Otherwise: assume session expired / invalid refresh token; recover.
      clearKeycloakCookieArtifacts();
      clearStoredKeycloakTokens();

      const loginOnFailure = opts.loginOnFailure !== false;
      if (loginOnFailure) {
        // Prevent tight loops if multiple parts of the app trigger refresh at once.
        recoveryUntilTs = safeNow() + 20_000;
        try {
          await doLogin(keycloak, !!opts.forcePromptLogin);
        } catch (loginError) {
          return {
            ok: false,
            reason: `login_failed:${describeError(loginError)}`,
            error: loginError,
          };
        }
      }

      return {ok: false, reason: `refresh_failed:${errStr}`, error};
    } finally {
      releaseRefreshLock();
      inFlightRefresh = null;
    }
  })();

  return inFlightRefresh;
}

export function startTokenRefreshLoop(
  keycloak: KeycloakLike,
  {
    intervalMs = 30_000,
    minValiditySeconds = 60,
    onNetworkError,
  }: {
    intervalMs?: number;
    minValiditySeconds?: number;
    onNetworkError?: () => void;
  } = {}
): () => void {
  const id = window.setInterval(() => {
    // Avoid refreshing in background tabs when the browser throttles timers heavily.
    if (document.visibilityState === 'hidden') return;
    void refreshTokenOrRecover(keycloak, minValiditySeconds, {
      onNetworkError,
      loginOnFailure: true,
      forcePromptLogin: false,
    });
  }, intervalMs);

  return () => window.clearInterval(id);
}
